<template>
    <article
        :data-workforce-employee-id='employeeId'
        class="workforce-employee-card"
        :class="{
            'workforce-employee-card--itr': isItr,
            'workforce-employee-card--absent': employee.is_absent,
            'workforce-employee-card--draggable': draggable,
            'workforce-employee-card--dragging': dragging,
            'workforce-employee-card--compact': compact,
            'workforce-employee-card--overview': overview,
            'workforce-employee-card--search-result': searchActive,
            'workforce-employee-card--planned-projection': plannedProjection,
            'workforce-employee-card--timesheet-projection': timesheetProjection,
            [`workforce-employee-card--timesheet-${timesheetFactSeverity}`]:
                timesheetProjection
        }"
        :data-workforce-search-result="searchActive ? 'true' : null"
        :data-workforce-assignment-kind="assignmentKind || null"
        :draggable="draggable ? 'true' : 'false'"
        :tabindex="cardIsInteractive ? '0' : null"
        :role="cardIsInteractive ? 'button' : null"
        :aria-label="cardAriaLabel"
        :title="cardTooltip"
        @click="openCard"
        @keydown.enter.self.prevent="openCard"
        @keydown.space.self.prevent="openCard"
        @contextmenu.prevent="$emit('contextmenu', $event, employee)"
        @dragstart="onDragStart"
        @dragend="$emit('dragend')"
    >
        <template v-if="overview">
            <i
                v-if="draggable"
                class="fas fa-grip-lines workforce-employee-card__overview-grip"
                aria-hidden="true"
            ></i>
            <span class="workforce-employee-card__overview-identity">
                <span
                    class="workforce-employee-card__overview-name"
                    :title="cardTooltip || employeeName"
                >
                    <workforce-search-highlight
                        :text="overviewEmployeeName"
                        :query="searchQuery"
                    ></workforce-search-highlight>
                </span>
                <small
                    v-if="employeeMetaLabel || employee.hiring_form === 'gph'"
                    class="workforce-employee-card__overview-meta"
                    :title="employeeMetaTitle"
                >
                    <span class="workforce-hiring-meta-text">{{ employeeMetaLabel }}</span>
                    <employee-hiring-badge :value="employee.hiring_form" />
                </small>
            </span>
            <span class="workforce-employee-card__overview-statuses">
                <i
                    v-if="employee.scheduled_termination"
                    class="fas fa-user-clock"
                    :title="scheduledTerminationLabel"
                    aria-label="Есть запланированное увольнение"
                ></i>
                <i
                    v-if="plannedProjection || hasOverviewPlannedMove || planSlice || employee.workforce_plan_next"
                    class="far fa-calendar-alt"
                    :title="overviewPlannedTitle"
                    aria-label="Есть плановое перемещение"
                ></i>
                <i
                    v-if="employee.is_absent"
                    class="far fa-clock"
                    :title="absenceTooltip"
                    aria-label="Временно отсутствует"
                ></i>
                <i
                    v-if="assignment && !assignmentIsPrimary && !plannedProjection"
                    class="fas fa-link"
                    title="Дополнительное назначение"
                    aria-label="Дополнительное назначение"
                ></i>
                <i
                    v-if="timesheetProjection || employee.has_timesheet_fact_outside_assignment"
                    class="fas fa-file-signature"
                    :title="timesheetFactTooltip"
                    aria-label="Есть факт присутствия по табелю"
                ></i>
                <i
                    v-if="employee.has_hidden_assignments"
                    class="fas fa-lock"
                    title="Есть назначения на недоступных вам объектах"
                    aria-label="Есть скрытые назначения"
                ></i>
            </span>
        </template>

        <template v-else>
        <div class="workforce-employee-card__top">
            <span class="workforce-employee-card__name">
                <workforce-search-highlight
                    :text="employeeName"
                    :query="searchQuery"
                ></workforce-search-highlight>
            </span>
            <span
                v-if="hasPriority"
                class="workforce-employee-card__priority"
                :class="{
                    'workforce-employee-card__priority--reserve':
                        employee.lifecycle_status === 'reserve'
                }"
                :title="`Приоритет: ${employee.priority}`"
            >
                {{ priorityLabel }}
            </span>
        </div>

        <div
            v-if="employeeMetaLabel || employee.hiring_form === 'gph'"
            class="workforce-employee-card__meta"
            :title="employeeMetaTitle"
        >
            <workforce-search-highlight
                :text="employeeMetaLabel"
                :query="searchQuery"
                class="workforce-hiring-meta-text"
            ></workforce-search-highlight>
            <employee-hiring-badge :value="employee.hiring_form" />
        </div>

        <div
            v-if="!compact && primaryContact"
            class="workforce-employee-card__contact"
        >
            <i :class="primaryContactIcon" aria-hidden="true"></i>
            <EmployeePhoneMenu v-if="contactType(primaryContact) === 'phone'"
                               :telegram-username="telegramUsername"
                               :value="primaryContact.value || primaryContact.contact || primaryContact.text">
                <workforce-search-highlight :text="primaryContactValue" :query="searchQuery" />
            </EmployeePhoneMenu>
            <workforce-search-highlight
                v-else
                :text="primaryContactValue"
                :query="searchQuery"
            ></workforce-search-highlight>
        </div>

        <div
            v-if="assignment && !plannedProjection"
            class="workforce-employee-card__assignment"
        >
            <span
                class="workforce-employee-card__assignment-kind"
                :class="{
                    'workforce-employee-card__assignment-kind--additional':
                        !assignmentIsPrimary,
                    'workforce-employee-card__assignment-kind--plan': planSlice
                }"
            >
                <i
                    :class="
                        planSlice
                            ? 'far fa-calendar-alt'
                            : assignmentIsPrimary
                            ? 'fas fa-map-marker-alt'
                            : 'fas fa-link'
                    "
                    aria-hidden="true"
                ></i>
                {{ planSlice ? 'План' : (assignmentIsPrimary ? 'Основное' : 'Доп.') }}
            </span>
            <span
                v-if="otherVisibleAssignmentCount"
                class="workforce-employee-card__assignment-more"
                :title="otherAssignmentsTitle"
            >
                Ещё {{ otherVisibleAssignmentCount }}
            </span>
            <span
                v-if="employee.has_hidden_assignments"
                class="workforce-employee-card__assignment-hidden"
                title="Есть назначения на недоступных вам объектах"
            >
                <i class="fas fa-lock" aria-hidden="true"></i>
                <span class="sr-only">Есть скрытые назначения</span>
            </span>
        </div>

        <div
            v-if="employee.is_absent"
            class="workforce-employee-card__absence"
            :title="absenceTooltip"
        >
            <i class="far fa-clock" aria-hidden="true"></i>
            {{ employee.absence_label || 'Временно отсутствует' }}
        </div>

        <div
            v-if="employee.lifecycle_status === 'unverified'"
            class="workforce-employee-card__notice workforce-employee-card__notice--warning"
        >
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
            Непроверенный
        </div>

        <div
            v-if="employee.scheduled_termination"
            class="workforce-employee-card__notice workforce-employee-card__notice--warning"
            :title="scheduledTerminationLabel"
        >
            <i class="fas fa-user-clock" aria-hidden="true"></i>
            {{ scheduledTerminationLabel }}
        </div>

        <div
            v-if="employee.assignment_warning"
            class="workforce-employee-card__notice workforce-employee-card__notice--warning"
        >
            <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
            {{ employee.assignment_warning }}
        </div>

        <div
            v-if="timesheetProjection"
            class="workforce-employee-card__timesheet-fact"
            :class="`workforce-employee-card__timesheet-fact--${timesheetFactSeverity}`"
            :title="timesheetFactTooltip"
        >
            <i class="fas fa-file-signature" aria-hidden="true"></i>
            <span>
                <strong>{{ timesheetFactValue }}</strong>
                <small v-if="unresolvedTimesheetFact">Нет карточки сотрудника</small>
                <small v-else>В табеле есть, на доске не распределён</small>
            </span>
        </div>

        <div
            v-else-if="employee.has_timesheet_fact_outside_assignment"
            class="workforce-employee-card__notice workforce-employee-card__notice--timesheet"
            :title="timesheetFactTooltip"
        >
            <i class="fas fa-file-signature" aria-hidden="true"></i>
            Есть в табеле другого места
        </div>

        <div
            v-if="employee.workforce_plan_next && !planSlice"
            class="workforce-employee-card__notice workforce-employee-card__notice--planned"
        >
            <i class="far fa-calendar-alt" aria-hidden="true"></i>
            {{ workforcePlanLabel(employee.workforce_plan_next) }}
        </div>

        <div
            v-for="move in plannedMoves"
            :key="`planned-${move.id}`"
            class="workforce-employee-card__notice workforce-employee-card__notice--planned"
        >
            <i class="far fa-calendar-alt" aria-hidden="true"></i>
            {{ plannedMoveLabel(move) }}
        </div>

        <button
            v-if="showConfirmPlannedMoveButton"
            type="button"
            class="workforce-employee-card__confirm-plan"
            :disabled="plannedMovePending"
            title="Подтвердить прибытие сотрудника"
            @click.stop="$emit('confirm-planned-move', employee)"
        >
            <b-spinner v-if="plannedMovePending" small></b-spinner>
            <i v-else class="fas fa-check" aria-hidden="true"></i>
            Подтвердить прибытие
        </button>

        <button
            v-if="showTimesheetAction"
            type="button"
            class="workforce-employee-card__timesheet-action"
            :class="{
                'workforce-employee-card__timesheet-action--blocking':
                    timesheetFactSeverity === 'blocking'
            }"
            @click.stop="runTimesheetAction"
        >
            <i
                :class="unresolvedTimesheetFact || timesheetFactSeverity === 'blocking'
                    ? 'fas fa-exclamation-circle'
                    : 'fas fa-link'"
                aria-hidden="true"
            ></i>
            {{ unresolvedTimesheetFact
                ? 'Сопоставить сотрудника'
                : (timesheetFactSeverity === 'blocking'
                    ? 'Разобрать расхождение'
                    : 'Оформить назначение') }}
        </button>

        <button
            v-if="showMoveButton"
            type="button"
            class="workforce-employee-card__move"
            @click.stop="$emit('move', employee)"
        >
            <i class="fas fa-exchange-alt" aria-hidden="true"></i>
            {{ moveLabel }}
        </button>
        </template>
    </article>
</template>

<script>
import EmployeeHiringBadge from './EmployeeHiringBadge.vue';
import {formatPhone} from '@/utils/phone';
import EmployeePhoneMenu from '@/components/EmployeePhoneMenu.vue';
import {employeeTelegramUsername} from '@/utils/employeeTelegram';
import employeeNameFormat from '@/utils/employeeNameFormat';
import WorkforceSearchHighlight from './WorkforceSearchHighlight.vue';
import {employeeAbsenceTooltip} from './employeeAbsencePresentation';

export default {
    name: 'EmployeeBoardCard',
    inject: {
        workforceEmployeeNameFormat: {
            default: () => ({
                value: employeeNameFormat.DEFAULT_EMPLOYEE_NAME_FORMAT
            })
        }
    },
    components: {
        EmployeePhoneMenu,
        EmployeeHiringBadge,
        WorkforceSearchHighlight
    },
    props: {
        employee: {
            type: Object,
            required: true
        },
        canMove: {
            type: Boolean,
            default: false
        },
        mobile: {
            type: Boolean,
            default: false
        },
        compact: {
            type: Boolean,
            default: false
        },
        overview: {
            type: Boolean,
            default: false
        },
        moveLabel: {
            type: String,
            default: 'Назначения'
        },
        allowReserveMove: {
            type: Boolean,
            default: false
        },
        draggingEmployeeId: {
            type: [String, Number],
            default: null
        },
        draggingAssignmentId: {
            type: [String, Number],
            default: null
        },
        searchQuery: {
            type: String,
            default: ''
        },
        plannedMovePending: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        telegramUsername() { return employeeTelegramUsername(this.employee) || ''; },
        isItr() {
            return this.employee.multi_assignment_eligible === true;
        },
        employeeName() {
            return employeeNameFormat.formatEmployeeName(
                this.employee,
                this.workforceEmployeeNameFormat.value
            );
        },
        employeeId() {
            return this.employee.employee_id || this.employee.id;
        },
        overviewEmployeeName() {
            return this.employeeName;
        },
        skillDetails() {
            const details = Array.isArray(this.employee.skill_details)
                ? this.employee.skill_details
                : [];
            return details.filter(Boolean).map(skill => (
                typeof skill === 'string'
                    ? {name: skill, description: null}
                    : skill
            ));
        },
        skillNames() {
            const values = Array.isArray(this.employee.skills)
                ? this.employee.skills
                : [];
            const names = values.map(skill => (
                typeof skill === 'string' ? skill : skill && skill.name
            )).filter(Boolean);
            return Array.from(new Set(names));
        },
        skillLabel() {
            return this.skillNames.join('/');
        },
        skillTitle() {
            if (!this.skillNames.length) return '';
            const descriptions = new Map(this.skillDetails.map(skill => [
                String(skill.name || '').trim().toLocaleUpperCase('ru-RU'),
                skill.description || null
            ]));
            return this.skillNames.map(name => {
                const description = descriptions.get(
                    String(name).trim().toLocaleUpperCase('ru-RU')
                );
                return description ? `${name} — ${description}` : name;
            }).join('\n');
        },
        employeeMetaLabel() {
            return [
                this.employee.position,
                this.skillLabel,
                this.employee.citizenship
            ].filter(Boolean).join(' · ');
        },
        employeeMetaTitle() {
            return [
                this.employee.hiring_form === 'gph' ? 'Форма найма: ГПХ' : null,
                this.employee.position ? `Должность: ${this.employee.position}` : null,
                this.skillTitle ? `Навыки:\n${this.skillTitle}` : null,
                this.employee.citizenship
                    ? `Гражданство: ${this.employee.citizenship}`
                    : null
            ].filter(Boolean).join('\n\n');
        },
        plannedProjection() {
            return this.employee.workforce_planned_projection === true;
        },
        planSlice() {
            return this.employee.workforce_plan_mode === true;
        },
        timesheetProjection() {
            return this.employee.workforce_timesheet_projection === true;
        },
        unresolvedTimesheetFact() {
            return this.employee.workforce_unresolved_timesheet === true;
        },
        timesheetFact() {
            return this.employee.timesheet_fact || null;
        },
        timesheetFactSeverity() {
            const severity = this.timesheetFact && this.timesheetFact.severity;
            return ['blocking', 'warning', 'info'].indexOf(severity) >= 0
                ? severity
                : 'warning';
        },
        timesheetFactValue() {
            const fact = this.timesheetFact || {};
            const values = [];
            const minutes = Number(fact.worked_minutes || 0);
            if (minutes > 0) {
                const hours = minutes / 60;
                values.push(`${Number.isInteger(hours) ? hours : hours.toFixed(1)} ч`);
            }
            (Array.isArray(fact.codes) ? fact.codes : []).forEach(code => values.push(code));
            return `По табелю: ${values.join(' + ') || 'есть отметка'}`;
        },
        timesheetFactsForTooltip() {
            if (this.timesheetProjection && this.timesheetFact) return [this.timesheetFact];
            return Array.isArray(this.employee.timesheet_facts)
                ? this.employee.timesheet_facts.filter(fact => fact && fact.severity)
                : [];
        },
        timesheetFactTooltip() {
            const facts = this.timesheetFactsForTooltip;
            if (!facts.length) return 'Есть факт присутствия по табелю';
            return facts.map(fact => {
                const details = [];
                if (fact.work_date) details.push(this.formatDate(fact.work_date));
                if (fact.object_name) details.push(fact.object_name);
                else if (fact.object_visible === false) details.push('Другой объект');
                const minutes = Number(fact.worked_minutes || 0);
                if (minutes > 0) details.push(`${minutes / 60} ч`);
                if (Array.isArray(fact.codes) && fact.codes.length) {
                    details.push(fact.codes.join(', '));
                }
                if (Array.isArray(fact.authors) && fact.authors.length) {
                    details.push(`Автор: ${fact.authors.join(', ')}`);
                }
                return details.join(' · ');
            }).join('; ');
        },
        cardIsInteractive() {
            return !this.unresolvedTimesheetFact;
        },
        cardAriaLabel() {
            return this.cardIsInteractive
                ? `Открыть сведения: ${this.employeeName}`
                : `${this.employeeName}. ${this.timesheetFactValue}`;
        },
        searchActive() {
            return Boolean(String(this.searchQuery || '').trim());
        },
        assignment() {
            return this.employee.assignment ||
                this.employee.assignment_context ||
                null;
        },
        assignmentId() {
            return this.assignmentIdFor(this.assignment);
        },
        assignmentKind() {
            return this.assignmentKindFor(this.assignment);
        },
        assignmentIdentity() {
            return this.assignmentKeyFor(this.assignment);
        },
        assignmentIsPrimary() {
            return this.assignmentKind !== 'additional';
        },
        draggable() {
            return this.canMove &&
                this.assignmentIsPrimary &&
                !this.plannedProjection &&
                !this.timesheetProjection &&
                !this.mobile;
        },
        dragging() {
            if (
                this.draggingAssignmentId !== null &&
                this.draggingAssignmentId !== undefined &&
                this.assignmentIdentity
            ) {
                return this.draggingEmployeeId !== null &&
                    String(this.draggingEmployeeId) === String(this.employeeId) &&
                    this.assignmentIsPrimary &&
                    String(this.draggingAssignmentId) === this.assignmentIdentity;
            }
            return this.draggingEmployeeId !== null &&
                String(this.draggingEmployeeId) === String(this.employeeId) &&
                this.assignmentIsPrimary;
        },
        showMoveButton() {
            return this.canMove &&
                !this.plannedProjection &&
                !this.timesheetProjection &&
                (
                    this.employee.lifecycle_status !== 'reserve' ||
                    this.allowReserveMove
                );
        },
        showTimesheetAction() {
            if (!this.timesheetProjection) return false;
            if (this.unresolvedTimesheetFact || this.timesheetFactSeverity === 'blocking') {
                return Boolean(
                    this.timesheetFact &&
                    this.timesheetFact.can_open_timesheet &&
                    this.timesheetFact.timesheet_id
                );
            }
            return this.canMove;
        },
        showConfirmPlannedMoveButton() {
            const move = this.employee.planned_move;
            return Boolean(
                this.plannedProjection &&
                (this.canMove || this.employee.can_confirm_planned_move === true) &&
                move &&
                move.id
            );
        },
        plannedMoves() {
            const moves = Array.isArray(this.employee.planned_moves)
                ? this.employee.planned_moves
                : (this.employee.planned_move ? [this.employee.planned_move] : []);
            return moves.filter(move => move && move.status === 'planned');
        },
        hasOverviewPlannedMove() {
            return this.plannedMoves.length > 0;
        },
        overviewPlannedTitle() {
            if (this.plannedProjection && this.employee.planned_move) {
                return this.plannedMoveLabel(this.employee.planned_move);
            }
            return this.plannedMoves.map(this.plannedMoveLabel).join('; ') ||
                'Есть плановое перемещение';
        },
        absenceTooltip() {
            return employeeAbsenceTooltip(
                this.employee.absence,
                this.employee.absence_label
            );
        },
        cardTooltip() {
            const values = [this.employeeName];
            if (this.isItr) values.push('ИТР — инженерно-технический работник');
            if (this.employeeMetaTitle) values.push(this.employeeMetaTitle);
            if (
                this.timesheetProjection ||
                this.employee.has_timesheet_fact_outside_assignment
            ) {
                values.push(this.timesheetFactTooltip);
            }
            if (this.employee.is_absent) values.push(this.absenceTooltip);
            if (this.employee.scheduled_termination) {
                values.push(this.scheduledTerminationLabel);
            }
            return values.length > 1 ? values.join('. ') : null;
        },
        scheduledTerminationLabel() {
            const value = this.employee.scheduled_termination;
            return value && value.terminated_on
                ? `Увольнение ${this.formatDate(value.terminated_on)}`
                : 'Увольнение запланировано';
        },
        hasPriority() {
            return this.employee.priority !== null &&
                this.employee.priority !== undefined &&
                this.employee.priority !== '';
        },
        priorityLabel() {
            return this.employee.lifecycle_status === 'reserve'
                ? `Приоритет ${this.employee.priority}`
                : this.employee.priority;
        },
        primaryContact() {
            const contacts = Array.isArray(this.employee.contacts)
                ? this.employee.contacts.filter(Boolean)
                : [];
            return contacts.find(contact => contact.is_primary) ||
                contacts.find(contact => this.contactType(contact) === 'phone') ||
                contacts.find(contact => this.contactType(contact) === 'email') ||
                contacts[0] ||
                null;
        },
        primaryContactValue() {
            const contact = this.primaryContact || {};
            const value = contact.value || contact.contact || contact.text || '';
            return this.contactType(contact) === 'phone' ? formatPhone(value) : value;
        },
        primaryContactIcon() {
            const type = this.contactType(this.primaryContact);
            if (type === 'phone') return 'fas fa-phone';
            if (type === 'email') return 'fas fa-envelope';
            if (type === 'telegram') return 'fab fa-telegram-plane';
            return 'fas fa-address-card';
        },
        otherVisibleAssignments() {
            const assignments = Array.isArray(this.employee.visible_assignments)
                ? this.employee.visible_assignments
                : [];
            const currentKey = this.assignmentIdentity;
            return assignments.filter(assignment => (
                !currentKey || this.assignmentKeyFor(assignment) !== currentKey
            ));
        },
        otherVisibleAssignmentCount() {
            return this.otherVisibleAssignments.length;
        },
        otherAssignmentsTitle() {
            const names = this.otherVisibleAssignments.map(assignment => (
                assignment.target_name ||
                assignment.name ||
                'Другое назначение'
            ));
            return names.join(', ');
        }
    },
    methods: {
        openCard() {
            if (!this.cardIsInteractive) return;
            this.$emit('open', this.employee);
        },
        runTimesheetAction() {
            if (!this.unresolvedTimesheetFact && this.timesheetFactSeverity !== 'blocking') {
                this.$emit('move', this.employee);
                return;
            }
            const timesheetId = this.timesheetFact && this.timesheetFact.timesheet_id;
            if (!timesheetId) return;
            const navigation = this.$router.push({
                name: 'timesheet',
                params: {timesheetId: String(timesheetId)}
            });
            if (navigation && typeof navigation.catch === 'function') {
                navigation.catch(() => {});
            }
        },
        plannedMoveLabel(move) {
            const date = this.formatDate(move.planned_for);
            const destination = move.target_name || {
                home: 'домой',
                requires_distribution: 'требует распределения',
                object_group: 'группа объектов',
                object: 'объект'
            }[move.target_type] || 'новое место';
            if (this.plannedProjection) {
                return `Ожидается с ${date}`;
            }
            return `Запланировано на ${date}: ${destination}`;
        },
        workforcePlanLabel(plan) {
            const date = this.formatDate(plan && plan.starts_on);
            let destination = 'план не заполнен';
            if (plan && plan.state_type === 'object') {
                destination = plan.target_name || 'объект';
            } else if (plan && plan.state_type === 'requires_distribution') {
                destination = '❌ не распределён';
            } else if (plan && plan.state_type === 'rest') {
                destination = 'ОТДХ';
            } else if (plan && plan.state_type === 'loupe') {
                destination = plan.loupe_reason
                    ? `🔍 Выбыл из планирования: ${plan.loupe_reason}`
                    : '🔍 Выбыл из планирования';
            }
            return `План с ${date}: ${destination}`;
        },
        contactType(contact) {
            return String(
                contact && (contact.contact_type || contact.type) || ''
            ).toLowerCase();
        },
        assignmentIdFor(assignment) {
            return assignment
                ? (assignment.assignment_id || assignment.id || null)
                : null;
        },
        assignmentKindFor(assignment) {
            if (!assignment) {
                return '';
            }
            if (assignment.kind) {
                return String(assignment.kind);
            }
            if (assignment.assignment_kind) {
                return String(assignment.assignment_kind);
            }
            return assignment.is_primary === true ? 'primary' : 'additional';
        },
        assignmentKeyFor(assignment) {
            if (!assignment) {
                return '';
            }
            const kind = this.assignmentKindFor(assignment) || 'unknown';
            const assignmentId = this.assignmentIdFor(assignment);
            if (assignmentId !== null && assignmentId !== undefined) {
                return `${kind}:${assignmentId}`;
            }
            const targetType = assignment.target_type || assignment.type || 'none';
            const targetId = assignment.target_id ||
                assignment.building_id ||
                assignment.object_group_id ||
                'common';
            return `${kind}:${targetType}:${targetId}`;
        },
        formatDate(value) {
            const parts = String(value || '').split('-');
            return parts.length === 3
                ? `${parts[2]}.${parts[1]}.${parts[0]}`
                : String(value || '');
        },
        onDragStart(event) {
            if (event.target.closest && event.target.closest('.employee-phone-menu')) {
                event.preventDefault();
                return;
            }
            if (!this.draggable) {
                event.preventDefault();
                return;
            }
            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(this.employeeId));
            }
            this.$emit('dragstart', this.employee);
        }
    }
};
</script>

<style scoped>
.workforce-employee-card__meta, .workforce-employee-card__overview-meta { display: flex; align-items: baseline; min-width: 0; }
.workforce-hiring-meta-text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.workforce-employee-card {
    position: relative;
    padding: 10px 11px;
    border: 1px solid #d8dee7;
    border-radius: 9px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(31, 45, 61, 0.06);
    color: #243447;
    cursor: pointer;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.workforce-employee-card:hover,
.workforce-employee-card:focus {
    border-color: #8db8e8;
    box-shadow: 0 3px 9px rgba(37, 91, 145, 0.14);
}

.workforce-employee-card--draggable {
    cursor: grab;
}

.workforce-employee-card--draggable:active {
    cursor: grabbing;
}

.workforce-employee-card--overview {
    display: grid;
    min-width: 0;
    min-height: 27px;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 5px;
    padding: 3px 6px;
    border-radius: 4px;
    box-shadow: none;
    font-size: 0.68rem;
    line-height: 1.15;
}

.workforce-employee-card--overview.workforce-employee-card--planned-projection {
    border-style: dashed;
    opacity: 0.62;
}

.workforce-employee-card__overview-grip {
    color: #8a9bad;
    font-size: 0.58rem;
}

.workforce-employee-card__overview-identity {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.workforce-employee-card__overview-name {
    min-width: 0;
    overflow: hidden;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-employee-card__overview-meta {
    overflow: hidden;
    color: #71849a;
    font-size: 0.55rem;
    font-weight: 500;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-employee-card__overview-statuses {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: #56738f;
    font-size: 0.58rem;
}

.workforce-employee-card__overview-statuses .fa-calendar-alt {
    color: #6f5aa8;
}

.workforce-employee-card__overview-statuses .fa-clock {
    color: #b36b2c;
}

.workforce-employee-card__overview-statuses .fa-link {
    color: #2778bc;
}

.workforce-employee-card--dragging {
    opacity: 0.45;
}

.workforce-employee-card--planned-projection {
    border-color: #9eabb8;
    border-style: dashed;
    background: #f3f6f8;
    box-shadow: none;
    opacity: 0.7;
}

.workforce-employee-card--planned-projection:hover,
.workforce-employee-card--planned-projection:focus {
    border-color: #70869b;
    background: #f7f9fb;
    box-shadow: 0 3px 9px rgba(54, 75, 96, 0.12);
    opacity: 0.92;
}

.workforce-employee-card--timesheet-projection {
    border: 1px dashed #a86bc5;
    border-left-width: 4px;
    background: #fcf8ff;
    box-shadow: none;
    cursor: default;
}

.workforce-employee-card--timesheet-blocking {
    border-color: #cf5360;
    background: #fff8f8;
}

.workforce-employee-card--timesheet-info {
    border-color: #6c93b7;
    background: #f7fbff;
}

.workforce-employee-card--absent {
    border-left: 4px solid #f0ad4e;
}

.workforce-employee-card--compact {
    padding: 7px 9px;
    border-radius: 7px;
}

.workforce-employee-card--compact .workforce-employee-card__name {
    font-size: 0.82rem;
}

.workforce-employee-card--compact .workforce-employee-card__meta {
    margin-top: 2px;
    overflow: hidden;
    font-size: 0.7rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-employee-card--search-result {
    border-color: #e0ad21;
    background: #fffbea;
    box-shadow: 0 0 0 1px rgba(224, 173, 33, 0.22);
}

.workforce-employee-card--search-current {
    border-color: #d97706;
    background: #fff3bf;
    box-shadow:
        0 0 0 3px rgba(217, 119, 6, 0.42),
        0 8px 22px rgba(112, 65, 8, 0.2);
}

.workforce-employee-card--remote-changed {
    animation: workforce-remote-change 3.8s ease-out;
}

.workforce-employee-card--just-moved {
    animation: workforce-just-moved 3.1s ease-in-out;
}

@keyframes workforce-remote-change {
    0%, 28% {
        border-color: #47a3ff;
        box-shadow: 0 0 0 3px rgba(71, 163, 255, 0.25),
            0 5px 14px rgba(33, 93, 153, 0.2);
        background: #f2f8ff;
    }
    100% {
        border-color: #d8e1ea;
        box-shadow: 0 2px 5px rgba(31, 55, 77, 0.08);
        background: #fff;
    }
}

@keyframes workforce-just-moved {
    0%, 100% {
        border-color: #d8dee7;
        background: #fff;
        box-shadow: 0 1px 2px rgba(31, 45, 61, 0.06);
    }
    12%, 38%, 64% {
        border-color: #28a745;
        background: #e7f8eb;
        box-shadow:
            0 0 0 4px rgba(40, 167, 69, 0.22),
            0 8px 18px rgba(32, 120, 53, 0.18);
    }
    25%, 51%, 77% {
        border-color: #8fd19e;
        background: #f7fff9;
        box-shadow: 0 0 0 1px rgba(40, 167, 69, 0.1);
    }
}

.workforce-employee-card--compact .workforce-employee-card__priority {
    min-width: 20px;
    min-height: 18px;
    padding: 0 5px;
    font-size: 0.68rem;
}

.workforce-employee-card--compact .workforce-employee-card__absence,
.workforce-employee-card--compact .workforce-employee-card__notice {
    margin-top: 5px;
    padding: 3px 5px;
    font-size: 0.67rem;
}

.workforce-employee-card--compact .workforce-employee-card__move {
    margin-top: 6px;
}

.workforce-employee-card__top {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.workforce-employee-card__name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.workforce-employee-card--itr .workforce-employee-card__name,
.workforce-employee-card--itr .workforce-employee-card__overview-name {
    background: #fffbea;
    border-radius: 3px;
}

.workforce-employee-card__priority {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    min-height: 22px;
    padding: 1px 6px;
    border-radius: 999px;
    background: #e8f1fb;
    color: #205c99;
    font-size: 0.76rem;
    font-weight: 700;
}

.workforce-employee-card__priority--reserve {
    background: #fff1cc;
    color: #825700;
    white-space: nowrap;
}

.workforce-employee-card__meta {
    margin-top: 4px;
    color: #6c7784;
    font-size: 0.78rem;
    line-height: 1.25;
}

.workforce-employee-card__contact {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    margin-top: 7px;
    padding: 5px 7px;
    border-radius: 6px;
    background: #f3f6f9;
    color: #50677d;
    font-size: 0.73rem;
}

.workforce-employee-card__contact i {
    flex: 0 0 14px;
    width: 14px;
    color: #6c8aa8;
    text-align: center;
}

.workforce-employee-card__contact span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-employee-card__assignment {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 6px;
}

.workforce-employee-card__assignment-kind,
.workforce-employee-card__assignment-more,
.workforce-employee-card__assignment-hidden {
    display: inline-flex;
    align-items: center;
    min-height: 20px;
    padding: 2px 6px;
    border-radius: 999px;
    background: #e8f1fb;
    color: #285f96;
    font-size: 0.66rem;
    font-weight: 700;
    line-height: 1;
}

.workforce-employee-card__assignment-kind i {
    margin-right: 4px;
}

.workforce-employee-card__assignment-kind--additional {
    background: #e8f6f3;
    color: #287565;
}

.workforce-employee-card__assignment-kind--plan {
    background: #fff0c7;
    color: #86610b;
}

.workforce-employee-card__assignment-more {
    background: #eef1f4;
    color: #657585;
}

.workforce-employee-card__assignment-hidden {
    width: 20px;
    justify-content: center;
    padding: 2px;
    background: #f0f1f3;
    color: #6d7680;
}

.workforce-employee-card__absence {
    margin-top: 7px;
    color: #9a5b00;
    font-size: 0.74rem;
    font-weight: 600;
}

.workforce-employee-card__absence i {
    margin-right: 3px;
}

.workforce-employee-card__notice {
    margin-top: 7px;
    padding: 5px 7px;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.3;
}

.workforce-employee-card__notice i {
    margin-right: 4px;
}

.workforce-employee-card__notice--warning {
    background: #fff4dc;
    color: #8f5908;
}

.workforce-employee-card__notice--timesheet {
    background: #f2eafb;
    color: #6e358d;
}

.workforce-employee-card__timesheet-fact {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 7px;
    padding: 6px 7px;
    border-radius: 6px;
    background: #f2eafb;
    color: #633781;
    font-size: 0.72rem;
    line-height: 1.25;
}

.workforce-employee-card__timesheet-fact > span {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.workforce-employee-card__timesheet-fact small {
    color: inherit;
    font-size: 0.66rem;
}

.workforce-employee-card__timesheet-fact--blocking {
    background: #fdebed;
    color: #9d2634;
}

.workforce-employee-card__timesheet-fact--info {
    background: #eaf3fb;
    color: #315f86;
}

.workforce-employee-card__timesheet-action {
    width: 100%;
    margin-top: 7px;
    padding: 5px 8px;
    border: 1px solid #8f5baa;
    border-radius: 6px;
    background: #fff;
    color: #6e358d;
    font-size: 0.72rem;
    font-weight: 700;
}

.workforce-employee-card__timesheet-action--blocking {
    border-color: #c94c59;
    color: #a22836;
}

.workforce-employee-card__notice--planned {
    background: #eaf3fd;
    color: #265f96;
}

.workforce-employee-card--planned-projection
    .workforce-employee-card__notice--planned {
    border: 1px dashed #9aaaba;
    background: rgba(255, 255, 255, 0.55);
    color: #52697f;
}

.workforce-employee-card__confirm-plan {
    width: 100%;
    margin-top: 8px;
    padding: 6px 8px;
    border: 1px solid #66aa76;
    border-radius: 6px;
    background: #edf9f0;
    color: #24713a;
    font-size: 0.74rem;
    font-weight: 700;
}

.workforce-employee-card__confirm-plan i,
.workforce-employee-card__confirm-plan .spinner-border {
    margin-right: 5px;
}

.workforce-employee-card__confirm-plan:disabled {
    border-color: #b7c0c8;
    background: #edf0f2;
    color: #78838d;
    cursor: not-allowed;
}

.workforce-employee-card__move {
    width: 100%;
    margin-top: 9px;
    padding: 6px 8px;
    border: 1px solid #b8c7d8;
    border-radius: 6px;
    background: #f8fafc;
    color: #2f5f8f;
    font-size: 0.78rem;
    font-weight: 600;
}

.workforce-employee-card__move i {
    margin-right: 5px;
}
</style>
